// src/features/system/orchestrator/api/snapshotApi.ts
//
// FE API layer for the remote snapshot & delta comparison endpoints.
//
// Follows the same pattern as scopeMigrateApi.ts — uses the shared `bff`
// client (auto JWT refresh, 401 retry, dual-header mode).
import { bff } from '@/lib/http/bff';

// ── Types ───────────────────────────────────────────────────────────────────

export type DeltaKind = 'new' | 'synced' | 'changed' | 'local_only';

export interface SnapshotNamespace {
  scope_namespace: string;
  count: number;
  delta: DeltaKind;
  /** Count on the local side (0 if namespace doesn't exist locally). */
  local_count: number;
}

export interface SnapshotScope {
  scope_namespace: string;
  scope_key: string;
  id_scope?: number;
  delta: 'new' | 'synced';
  [key: string]: unknown;
}

export interface SnapshotNamespaceSummary {
  remote_total: number;
  new_count: number;
  changed_count: number;
  synced_count: number;
  local_only_count: number;
}

export interface SnapshotNamespacesResult {
  id: string;
  source_env: string;
  captured_at: string;
  namespaces: SnapshotNamespace[];
  local_only_namespaces: SnapshotNamespace[];
  summary: SnapshotNamespaceSummary;
}

export interface SnapshotScopeDelta {
  newCount: number;
  syncedCount: number;
  localOnlyCount: number;
  totalRemote: number;
}

export interface SnapshotScopesResult {
  scope_namespace: string;
  scopes: SnapshotScope[];
  local_only: SnapshotScope[];
  summary: SnapshotScopeDelta;
}

export interface SnapshotPreviewCounts {
  scope_count: number;
  outcome_count: number;
  hot_stream_count: number;
  gcs_file_count: number;
  archive_file_count: number;
}

// ── Params ──────────────────────────────────────────────────────────────────

export interface SnapshotBaseParams {
  remote_url: string;
  source_env: string;
  tenant_id?: number;
  access_token?: string;
}

export interface SnapshotScopesParams extends SnapshotBaseParams {
  scope_namespace: string;
  limit?: number;
}

export interface SnapshotPreviewParams extends SnapshotBaseParams {
  root_scope_ids: number[];
  include_outcomes?: boolean;
  include_hot_stream?: boolean;
}

// ── Remote Health / Diagnostics ─────────────────────────────────────────

export interface RemoteHealthCheck {
  step: 'reachable' | 'env_info' | 'auth' | 'authenticated_read';
  ok: boolean;
  detail: string;
  latency_ms?: number;
  native_code?: string | null;
  method?: string | null;
  service_jwt_available?: boolean;
  has_access_token?: boolean;
  status?: number;
  data?: Record<string, unknown>;
  /** Root-cause classification for authenticated_read failures. */
  cause?: 'secret_mismatch' | 'token_expired' | 'auth_rejected' | 'permission_denied' | 'remote_error' | 'unknown';
  /** Human-readable remediation guidance. */
  remediation?: string;
}

export interface RemoteHealthResult {
  reachable: boolean;
  healthy: boolean;
  source_env: string;
  remote_url: string;
  checks: RemoteHealthCheck[];
  checked_at: string;
}

// ── API Calls ───────────────────────────────────────────────────────────────

/**
 * Capture remote namespace listing + compare against local.
 * Returns delta-annotated namespaces with summary.
 */
export const fetchSnapshotNamespaces = (
  params: SnapshotBaseParams,
): Promise<SnapshotNamespacesResult> =>
  bff.post<SnapshotNamespacesResult>(
    '/orchestrator/migrate/scopes/snapshot/namespaces',
    params as any,
    { timeoutMs: 30_000 },
  );

/**
 * Fetch + compare remote scope keys for a specific namespace.
 * Returns delta-annotated scopes with new/synced/local_only indicators.
 */
export const fetchSnapshotScopes = (
  params: SnapshotScopesParams,
): Promise<SnapshotScopesResult> =>
  bff.post<SnapshotScopesResult>(
    '/orchestrator/migrate/scopes/snapshot/scopes',
    params as any,
    { timeoutMs: 30_000 },
  );

/**
 * Fetch remote preview counts through the authenticated snapshot channel.
 * Proxies the remote's preview endpoint — same shape as local preview.
 */
export const fetchSnapshotPreviewCounts = (
  params: SnapshotPreviewParams,
): Promise<SnapshotPreviewCounts> =>
  bff.post<SnapshotPreviewCounts>(
    '/orchestrator/migrate/scopes/snapshot/preview',
    params as any,
    { timeoutMs: 30_000 },
  );

/**
 * Run multi-step connectivity diagnostics against a remote BFF.
 * Returns structured check results (reachable → auth → read).
 */
export const diagnoseRemote = (
  params: SnapshotBaseParams,
): Promise<RemoteHealthResult> =>
  bff.post<RemoteHealthResult>(
    '/orchestrator/migrate/scopes/diagnose',
    params as any,
    { timeoutMs: 15_000 },
  );
